import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Phone, User, Send, ServerOff } from 'lucide-react';
import Header from '../header';
import Footer from '../Footer';
import config from '../../config';
import ReCAPTCHA from "react-google-recaptcha";

const OfflinePage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', mobile: '' });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [formError, setFormError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.mobile) {
      setFormError('Please fill all fields.');
      return;
    }
    if (!captchaToken) {
      setFormError('Please verify you are human.');
      return;
    }
    setFormError(null);
    setIsSubmitting(true);

    try {
      const payload = {
        name: formData.name.trim(),
        mobile: formData.mobile,
        email: formData.email,
        timestamp: new Date().toISOString(),
        recaptcha_token: captchaToken
      };
      
      const response = await fetch(`${config.API_URL}/requests/`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setIsSubmitting(false);
        setIsSubmitted(true);
        // Reset form
        setFormData({ name: '', email: '', mobile: '' });
        setCaptchaToken(null);
      } else {
        setFormError('Failed to submit request. Please try again.');
        setIsSubmitting(false);
      }
    } catch (error) {
      setFormError('Network error. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#010411] text-slate-100 font-sans selection:bg-cyan-500 selection:text-black overflow-x-hidden relative flex flex-col">
      {/* Futuristic Cinematic Glowing Nodes Background */}
      <div className="absolute top-0 inset-x-0 h-[64rem] bg-gradient-to-b from-[#051139] via-[#01051c] to-[#010411] pointer-events-none z-0" />
      <div className="fixed top-0 left-1/4 w-[700px] h-[700px] bg-blue-600/10 rounded-full filter blur-[140px] pointer-events-none z-0" />
      <div className="fixed top-1/3 right-1/4 w-[800px] h-[800px] bg-cyan-600/10 rounded-full filter blur-[160px] pointer-events-none z-0" />
      
      {/* Global Navigation Header */}
      <Header />

      <main className="flex-grow flex items-center justify-center relative z-10 px-4 py-20 min-h-[80vh]">
        <div className="max-w-6xl w-full grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
          
          {/* 3D Animated Illustration / Message Section */}
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8 relative"
          >
            <motion.div 
              animate={{ 
                y: [0, -15, 0],
                rotateZ: [0, 3, -3, 0]
              }}
              transition={{ 
                duration: 6, 
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="inline-flex items-center justify-center p-5 bg-[#050a24]/80 border border-cyan-500/30 rounded-3xl text-cyan-400 backdrop-blur-xl shadow-[0_0_30px_rgba(34,211,238,0.2)]"
            >
              <ServerOff size={56} strokeWidth={1.5} />
            </motion.div>
            
            <div className="space-y-5 relative z-10">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-200 to-indigo-500 leading-tight pb-4">
                Service Available <br /> Only Offline
              </h1>
              <p className="text-lg sm:text-xl text-slate-300 leading-relaxed max-w-lg border-l-2 border-cyan-500/50 pl-4 drop-shadow-md">
                Our digital platforms are currently taking a breather, but our dedication to you never pauses. 
                <span className="text-cyan-400 font-semibold block mt-3">Leave your details below, and our expert agents will reach out to serve you shortly.</span>
              </p>
            </div>
            
            {/* Abstract 3D Floating Elements */}
            <div className="hidden lg:block absolute -right-10 top-0 h-full w-full pointer-events-none z-0 opacity-60" style={{ perspective: 1000 }}>
                <motion.div 
                  animate={{ 
                    rotateX: [0, 15, 0], 
                    rotateY: [0, 25, 0],
                    y: [0, -20, 0]
                  }}
                  transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute right-10 top-10 w-32 h-32 bg-gradient-to-br from-cyan-500/10 to-blue-600/10 rounded-2xl border border-white/5 backdrop-blur-md shadow-2xl"
                  style={{ transformStyle: 'preserve-3d' }}
                >
                  <div className="absolute inset-2 border border-cyan-500/20 rounded-xl" style={{ transform: 'translateZ(20px)' }}></div>
                </motion.div>
                
                <motion.div 
                  animate={{ 
                    rotateX: [0, -20, 0], 
                    rotateY: [0, -30, 0],
                    y: [0, 15, 0]
                  }}
                  transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                  className="absolute right-32 bottom-10 w-24 h-24 bg-gradient-to-tr from-indigo-500/10 to-purple-600/10 rounded-full border border-white/5 backdrop-blur-md shadow-2xl"
                  style={{ transformStyle: 'preserve-3d' }}
                >
                  <div className="absolute inset-3 border border-indigo-500/30 rounded-full" style={{ transform: 'translateZ(15px)' }}></div>
                </motion.div>
            </div>
          </motion.div>

          {/* Contact Form Section with 3D Card Effect */}
          <div style={{ perspective: 1200 }} className="w-full max-w-md mx-auto lg:ml-auto">
            <motion.div 
              initial={{ opacity: 0, y: 50, rotateX: 15 }}
              animate={{ opacity: 1, y: 0, rotateX: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <motion.div
                whileHover={{ scale: 1.02, rotateY: -2, rotateX: 2 }}
                transition={{ duration: 0.4 }}
                className="bg-[#050a24]/60 backdrop-blur-xl border border-white/10 rounded-[2rem] p-8 sm:p-10 shadow-[0_0_50px_rgba(14,165,233,0.1)] relative overflow-hidden"
                style={{ transformStyle: 'preserve-3d' }}
              >
                {/* Glossy overlay */}
                <div className="absolute inset-0 bg-gradient-to-tr from-white/5 via-transparent to-transparent pointer-events-none" />
                
                <div style={{ transform: 'translateZ(30px)' }}>
                  <h2 className="text-2xl font-bold text-white mb-8 flex items-center gap-3">
                    <span className="w-10 h-10 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400">
                      <User size={20} />
                    </span>
                    Request Callback
                  </h2>

                  <AnimatePresence mode="wait">
                    {isSubmitted ? (
                      <motion.div 
                        key="success"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        className="py-10 text-center space-y-5"
                      >
                        <div className="w-20 h-20 mx-auto bg-emerald-500/10 border border-emerald-500/20 rounded-full flex items-center justify-center text-emerald-400 mb-2 relative">
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", delay: 0.2, bounce: 0.5 }}
                          >
                            <Send size={32} className="ml-1" />
                          </motion.div>
                          {/* Success pulse */}
                          <motion.div
                            animate={{ scale: [1, 1.5], opacity: [0.5, 0] }}
                            transition={{ duration: 1.5, repeat: Infinity }}
                            className="absolute inset-0 bg-emerald-500/20 rounded-full"
                          />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-white">Request Received!</h3>
                          <p className="text-slate-400 text-sm mt-2">Our agent will contact you shortly.</p>
                        </div>
                      </motion.div>
                    ) : (
                      <motion.form 
                        key="form"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onSubmit={handleSubmit} 
                        className="space-y-6"
                      >
                        <div className="space-y-2">
                          <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 ml-1">Full Name</label>
                          <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-500 group-focus-within:text-cyan-400 transition-colors">
                              <User size={18} />
                            </div>
                            <input 
                              type="text" 
                              required
                              value={formData.name}
                              onChange={(e) => setFormData({...formData, name: e.target.value})}
                              className="w-full bg-[#03071e]/50 border border-white/10 rounded-xl py-3.5 pl-12 pr-4 text-white placeholder-slate-600 focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 transition-all text-sm"
                              placeholder="John Doe"
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 ml-1">Email Address</label>
                          <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-500 group-focus-within:text-cyan-400 transition-colors">
                              <Mail size={18} />
                            </div>
                            <input 
                              type="email" 
                              required
                              value={formData.email}
                              onChange={(e) => setFormData({...formData, email: e.target.value})}
                              className="w-full bg-[#03071e]/50 border border-white/10 rounded-xl py-3.5 pl-12 pr-4 text-white placeholder-slate-600 focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 transition-all text-sm"
                              placeholder="john@example.com"
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 ml-1">Mobile Number</label>
                          <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-500 group-focus-within:text-cyan-400 transition-colors">
                              <Phone size={18} />
                            </div>
                            <input 
                              type="tel" 
                              required
                              value={formData.mobile}
                              onChange={(e) => setFormData({...formData, mobile: e.target.value})}
                              className="w-full bg-[#03071e]/50 border border-white/10 rounded-xl py-3.5 pl-12 pr-4 text-white placeholder-slate-600 focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 transition-all text-sm"
                              placeholder="+91 98765 43210"
                            />
                          </div>
                        </div>

                        {/* ReCAPTCHA */}
                        <div className="flex flex-col items-center pt-2 space-y-2">
                          <ReCAPTCHA
                            sitekey={config.RECAPTCHA_SITE_KEY}
                            onChange={(token) => {
                              setCaptchaToken(token);
                              if (formError) setFormError(null);
                            }}
                            theme="dark"
                          />
                          {formError && (
                            <p className="text-red-400 text-[10px] font-semibold text-center">{formError}</p>
                          )}
                        </div>

                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          type="submit"
                          disabled={isSubmitting}
                          className="w-full mt-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold py-4 rounded-xl shadow-[0_0_20px_rgba(14,165,233,0.2)] hover:shadow-[0_0_30px_rgba(14,165,233,0.4)] transition-all flex items-center justify-center gap-2 text-sm uppercase tracking-wider disabled:opacity-50"
                        >
                          {isSubmitting ? (
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <>
                              Submit Details
                              <Send size={16} />
                            </>
                          )}
                        </motion.button>
                      </motion.form>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            </motion.div>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default OfflinePage;
